import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Mail, Phone, MessageCircle, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useTwoFactor, type TwoFactorMethod } from "@/hooks/useTwoFactor";

/** Self-service 2FA controls shown in the user settings ("Segurança"). */
export default function SecuritySettingsCard() {
  const { t } = useTranslation();
  const { state, loading, setOwnPreference, refetch } = useTwoFactor();
  const [saving, setSaving] = useState(false);

  // Always revalidate on mount so admin-side changes show up immediately.
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sourceLabel =
    state.source === "admin" || state.source === "role" || state.source === "global"
      ? t(`security.source.${state.source}`)
      : t("security.source.user");

  const update = async (enabled: boolean, method: TwoFactorMethod) => {
    setSaving(true);
    try {
      await setOwnPreference(enabled, method);
      toast.success(enabled ? t("security.twoFactor.savedOn") : t("security.twoFactor.savedOff"));
    } catch {
      toast.error(t("security.twoFactor.saveError"));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("security.twoFactor.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2 w-full max-w-full overflow-hidden">
      <div className="flex items-start justify-between gap-3 sm:gap-4 p-3.5 sm:p-4 rounded-2xl bg-muted/40 border border-border/40">
        <div className="flex gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold leading-tight">{t("security.twoFactor.title")}</p>
              <Badge
                variant={state.enabled ? "default" : "secondary"}
                className="text-[9px] px-2 py-0 shrink-0"
              >
                {state.enabled ? t("security.twoFactor.active") : t("security.twoFactor.inactive")}
              </Badge>
              {state.locked && (
                <span className="inline-flex items-center gap-1 text-[9px] text-muted-foreground">
                  <Lock className="w-2.5 h-2.5" /> {t("security.twoFactor.managed")}
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1 leading-snug break-words">
              {t("security.twoFactor.desc")}
            </p>
            <p className="text-[10px] text-muted-foreground/80 mt-1 break-words">{sourceLabel}</p>
          </div>
        </div>
        <Switch
          className="shrink-0 mt-0.5"
          checked={state.enabled}
          disabled={state.locked || saving}
          onCheckedChange={(v) => update(v, state.method)}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("security.twoFactor.method")}</Label>
        <Select
          value={state.method}
          disabled={!state.enabled || state.locked || saving}
          onValueChange={(v) => update(state.enabled, v as TwoFactorMethod)}
        >
          <SelectTrigger className="h-11 rounded-xl w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[2000]">
            <SelectItem value="email">
              <span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {t("security.twoFactor.email")}</span>
            </SelectItem>
            <SelectItem value="phone">
              <span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {t("security.twoFactor.smsSoon")}</span>
            </SelectItem>
            <SelectItem value="whatsapp">
              <span className="flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5" /> {t("security.twoFactor.whatsappSoon")}</span>
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground leading-snug">
          {t("security.twoFactor.methodHint")}
        </p>
      </div>

      {state.locked && (
        <p className="text-[11px] text-muted-foreground leading-snug">
          {state.enabled ? t("security.twoFactor.lockedOn") : t("security.twoFactor.lockedOff")}
        </p>
      )}
    </div>
  );
}
